import { computed, ref } from "vue";
import store from "../store";
import { KhkAdminService } from "../services/KhkAdminService";

/**
 * Admin brand switcher (#2122).
 *
 * The selected brand is the single client-side source of truth for the
 * X-Brand-Code header sent to management endpoints. It is hydrated from
 * GET /admin/me/brand — which resolves the backend-owned session brand used for
 * player-data — so the two paths stay in lock-step. Never guess the brand
 * client-side; always hydrate from the backend on login.
 */

// Module-level promise dedupes concurrent hydration (several router navigations
// firing before the first fetch resolves).
let loadPromise = null;

// A brand chosen on the logged-out login screen, stashed in localStorage so it
// survives the Okta redirect round-trip. Consumed once, post-auth, by
// ensureSelectedBrandLoaded (#2122).
const PENDING_LOGIN_BRAND_KEY = "pendingLoginBrandCode";

// Cross-tab signal: switchBrand persists the brand for the WHOLE admin session
// (a server-side row), so every other open tab's player-data requests silently
// switch to the new brand. Broadcasting the change lets those tabs reload instead
// of mixing new-brand data under stale old-brand UI / X-Brand-Code (#2122).
const BRAND_SWITCH_SIGNAL_KEY = "adminBrandSwitchSignal";

function broadcastBrandSwitch(code) {
    try {
        // The value must change to fire a 'storage' event in other tabs, so append
        // a nonce (a repeat switch to the same code still notifies).
        localStorage.setItem(BRAND_SWITCH_SIGNAL_KEY, `${code}:${Date.now()}`);
    } catch {
        // Best-effort — a broadcast failure just means other tabs update on their
        // own next navigation.
    }
}

/**
 * Register a listener that reloads THIS tab when another tab switches the admin's
 * brand. Call once at app startup. The backend session brand is shared across
 * tabs, so a switch elsewhere already changed what this tab's requests return —
 * reloading re-hydrates the store, header, and views to match (#2122).
 */
export function initBrandSwitchSync() {
    window.addEventListener("storage", event => {
        if (event.key !== BRAND_SWITCH_SIGNAL_KEY || !event.newValue) return;
        const code = event.newValue.split(":")[0];
        if (code && code !== store.state.selectedBrand?.code) {
            window.location.reload();
        }
    });
}

/**
 * Record the brand picked on the login screen. It is persisted to the backend on
 * the next post-auth hydration so the choice scopes the whole admin (player-data
 * + management), not just the client header. Pass a falsy code to clear it.
 * @param {string} code target brand code (e.g. "giddyup", "casinox")
 */
export function setPendingLoginBrand(code) {
    if (code) {
        localStorage.setItem(PENDING_LOGIN_BRAND_KEY, code);
    } else {
        localStorage.removeItem(PENDING_LOGIN_BRAND_KEY);
    }
}

/**
 * Ensure the current admin's selected brand is loaded into the store. Idempotent;
 * use `force` right after login since a previous admin's selection may be
 * persisted in localStorage. If the login screen recorded a pending brand, that
 * choice is persisted to the backend (and wins) before falling back to the
 * backend-owned selection. Never throws — on failure it keeps whatever is already
 * in the store (defaults to giddyup).
 * @param {boolean} force refetch even if already hydrated this session
 * @returns {Promise<object>} the selected brand { id, code, name, play_mode }
 */
export async function ensureSelectedBrandLoaded(force = false) {
    if (force) {
        loadPromise = null;
    }
    if (!loadPromise) {
        const service = new KhkAdminService();
        loadPromise = (async () => {
            // A login-screen choice is only honored on the forced post-login
            // hydration (LoginCallbackView). Router-guard hydrations ignore it:
            // a pending key found there is a leftover from an abandoned login
            // attempt — possibly another person's — not this admin's choice.
            const pendingCode = force ? localStorage.getItem(PENDING_LOGIN_BRAND_KEY) : null;
            try {
                // A login-screen choice wins: persist it so the backend session
                // brand agrees with the UI. Otherwise read the backend selection.
                const brand = pendingCode
                    ? await service.updateSelectedBrand(pendingCode)
                    : await service.getMySelectedBrand();
                localStorage.removeItem(PENDING_LOGIN_BRAND_KEY);
                store.commit("setSelectedBrand", brand);
                return brand;
            } catch (error) {
                console.error("Failed to load selected brand", error);
                loadPromise = null; // allow a later retry
                // Keep a login-screen choice across a transient failure (network /
                // 5xx) so a later forced hydration can still persist it — a blip
                // shouldn't silently discard the admin's explicit brand choice.
                // Drop it only on a 4xx (bad/rejected code) that will never
                // succeed, so it can't wedge every retry. Safe to keep: the login
                // page clears any leftover before the next login (#2122).
                const isClientError = error?.status >= 400 && error?.status < 500;
                if (!pendingCode || isClientError) {
                    localStorage.removeItem(PENDING_LOGIN_BRAND_KEY);
                }
                return store.state.selectedBrand;
            }
        })();
    }
    return loadPromise;
}

/**
 * Composable form for use in components (e.g. the topbar switcher).
 */
export function useSelectedBrand() {
    const service = new KhkAdminService();
    const brands = ref([]);
    const loadingBrands = ref(false);
    const switching = ref(false);

    const selectedBrand = computed(() => store.state.selectedBrand);

    // Load the list of brands available to switch between (management data).
    const loadBrands = async () => {
        loadingBrands.value = true;
        try {
            const configs = await service.listBrandConfigs();
            brands.value = (configs ?? []).map(c => ({
                id: c.brand_id,
                code: c.brand_code,
                name: c.brand_name,
                playMode: c.play_mode
            }));
        } catch (error) {
            console.error("Failed to load brands for switcher", error);
            brands.value = [];
        } finally {
            loadingBrands.value = false;
        }
        return brands.value;
    };

    /**
     * Switch the active brand: persist it on the backend, update the store (which
     * drives the X-Brand-Code header), then do a full page load so every view
     * re-fetches under the new brand.
     * @param {string} code target brand code
     * @param {string} [redirectTo] where to land after switching (e.g. the brand
     *   page sends the user to the dashboard). When omitted, reloads the current
     *   page. Honored even when the brand is unchanged so the picker always exits.
     */
    const switchBrand = async (code, redirectTo) => {
        if (!code) {
            if (redirectTo) window.location.assign(redirectTo);
            return;
        }
        // Always persist, even when code equals the store's current brand: the
        // store can drift from the backend session brand (e.g. a failed login
        // hydration), and re-persisting is the picker's one reliable way to
        // reconcile them. A no-op re-select just re-writes the same row (#2122).
        switching.value = true;
        try {
            const brand = await service.updateSelectedBrand(code);
            store.commit("setSelectedBrand", brand);
            broadcastBrandSwitch(brand.code);
            // Full page load is the simplest correct refresh: it re-runs every data
            // fetch with the new brand header and re-hydrates from the backend.
            if (redirectTo) {
                window.location.assign(redirectTo);
            } else {
                window.location.reload();
            }
        } catch (error) {
            switching.value = false;
            throw error;
        }
    };

    return {
        selectedBrand,
        brands,
        loadingBrands,
        switching,
        loadBrands,
        switchBrand,
        ensureSelectedBrandLoaded
    };
}

export default useSelectedBrand;
